import { Badge, DataGrid, Spinner } from '@ibsys/kit';
import { IconButton, Tooltip } from '@/shared/ui';
import type { DataGridLeafColumn, DataGridSortingState } from '@ibsys/kit';
import { CheckCircleIcon, PlayIcon, XCircleIcon } from 'lucide-react';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PipelineListItem, useDryRunPipeline } from '@/entities/jobs';
import { PipelineTasksList } from './PipelineTasksList';

type Props = {
  pipelines: PipelineListItem[];
  total: number;
  /** 0-based номер страницы */
  page: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

type DryRunResult = Record<string, 'success' | 'failed' | 'loading'>;

export const PipelinesTable: FC<Props> = ({ pipelines, total, page, perPage, onPageChange, onPageSizeChange }) => {
  const { t } = useTranslation(['common', 'pipelines']);

  const [dryRunResults, setDryRunResults] = useState<DryRunResult>({});
  const [sorting, setSorting] = useState<DataGridSortingState>([]);

  const dryRunMutation = useDryRunPipeline();

  const handleDryRun = useCallback(
    (e: React.MouseEvent, pipelineId: string) => {
      e.stopPropagation();
      setDryRunResults((prev) => ({ ...prev, [pipelineId]: 'loading' }));
      dryRunMutation.mutate(
        { nameOrId: pipelineId },
        {
          onSuccess: (data) => {
            setDryRunResults((prev) => ({
              ...prev,
              [pipelineId]: data.valid ? 'success' : 'failed',
            }));
          },
          onError: () => {
            setDryRunResults((prev) => ({ ...prev, [pipelineId]: 'failed' }));
          },
        },
      );
    },
    [dryRunMutation],
  );

  const formatDate = useCallback((isoDate: string): string => {
    return new Date(isoDate).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }, []);

  const formatTime = useCallback((isoDate: string): string => {
    return new Date(isoDate).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }, []);

  const columns: DataGridLeafColumn<PipelineListItem>[] = useMemo(
    () => [
      {
        id: 'tasksCount',
        header: () => <span className="sr-only">{'#'}</span>,
        accessor: 'tasksCount',
        size: 60,
        cell: ({ value }) => <span className="text-sm font-medium">{value as number}</span>,
      },
      {
        id: 'description',
        header: t('pipelines:table.description'),
        accessor: 'description',
        cell: ({ row }) => (
          <Tooltip tooltip={row.id}>
            <span className="font-semibold">{row.description || t('pipelines:noDescription')}</span>
          </Tooltip>
        ),
      },
      {
        id: 'inputType',
        header: t('pipelines:table.inputType'),
        accessor: 'inputType',
        size: 130,
        cell: ({ value }) => (
          <Badge variant="secondary" outline>
            {value as string}
          </Badge>
        ),
      },
      {
        id: 'createdTime',
        header: t('pipelines:table.createdTime'),
        accessor: (row) => row.createdAt,
        size: 120,
        cell: ({ value }) => <span>{formatTime(value as string)}</span>,
      },
      {
        id: 'createdDate',
        header: t('pipelines:table.createdDate'),
        accessor: (row) => row.createdAt,
        size: 130,
        cell: ({ value }) => <span>{formatDate(value as string)}</span>,
      },
      {
        id: 'validation',
        header: t('pipelines:table.validation'),
        accessor: 'id',
        size: 100,
        cell: ({ row }) => {
          const result = dryRunResults[row.id];
          if (result === 'loading') {
            return <Spinner className="size-4" />;
          }
          if (result === 'success') {
            return (
              <Tooltip tooltip={t('pipelines:validationSuccess')}>
                <CheckCircleIcon className="size-4 text-primary" />
              </Tooltip>
            );
          }
          if (result === 'failed') {
            return (
              <Tooltip tooltip={t('pipelines:validationFailed')}>
                <XCircleIcon className="size-4 text-destructive" />
              </Tooltip>
            );
          }
          return (
            <IconButton
              variant="ghost"
              size="icon"
              label={t('pipelines:validationPending')}
              icon={<PlayIcon className="size-4" />}
              onClick={(e) => handleDryRun(e, row.id)}
            />
          );
        },
      },
    ],
    [t, formatDate, formatTime, handleDryRun, dryRunResults],
  );

  return (
    <DataGrid
      columns={columns}
      dataSource={{
        mode: 'paginated',
        totalCount: total,
        page,
        pageSize: perPage,
        onPageChange,
        onPageSizeChange,
        pageSizeOptions: [10, 25, 50],
        data: pipelines,
      }}
      getRowId={(row) => row.id}
      sorting={sorting}
      onSortingChange={setSorting}
      renderExpandedRow={(row) => <PipelineTasksList pipelineNameOrId={row.id} />}
    />
  );
};
